import React from "react";
import PropTypes from "prop-types";
import styled from "styled-components";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSyncAlt } from "@fortawesome/free-solid-svg-icons";
import { connect } from "react-redux";
import { actions as assetsActions } from "../../actions/assets";
import { actions as contentActions } from "../../actions/content";
import LuciPropTypes from "../../propTypes/";
import { SearchBox } from "../../styled-components/";
import FilterOption from "../../constants/filterOptions";
import SortOption from "../../constants/sortOptions";
import {
  ControlBarContainer,
  ControlList,
  ControlListItem
} from "../../styled-components/ControlBar";
import MultiSelect from "../../styled-components/MultiSelect";
import { generateShortcutText } from "../../util/keyShortcutUtils";
import localize from "../../util/localizeUtils";
import DetailsListButton from "../DetailsListButton";
import { IconButton } from "../../styled-components";

const Mousetrap = window.Mousetrap;

const ControlListPadded = styled(ControlList)`
  padding: 0 0.5rem;
`;

const ReloadButton = styled.button`
  &:focus {
    outline: none;
  }
`;

class Header extends React.Component {
  constructor() {
    super();

    this.state = {
      searchTerms: ""
    };

    this.contextCombo = React.createRef();
    this.filterCombo = React.createRef();
  }

  componentDidMount() {
    this.props.changeSearchTerms("");
    this.bindShortcuts();
  }

  componentDidUpdate(nextProps) {
    if (
      this.props.isModifierActive === nextProps.isModifierActive &&
      nextProps.visible
    ) {
      this.focusSearchBox();
    }
  }

  componentWillUnmount() {
    this.unbindShortcuts();
  }

  onSearch = event => {
    if (event.key === "Enter") {
      this.props.changeSearchTerms(this.state.searchTerms);
      event.preventDefault(); // This prevents the "onChange" from being fired.
    } else if (!event.key) {
      this.setState({ searchTerms: event.target.value });
      if (event.target.value === "") {
        this.props.changeSearchTerms("");
      }
    }
  };

  onToggleDetails = () => {
    this.props.toggleDetailsView();
  };

  getSearchPlaceholder = () => {
    switch (this.props.assetType) {
      case "image":
        return localize("searchImagesPlaceholder");
      case "video":
        return localize("searchMoviesPlaceholder");
      default:
        return localize("searchPlaceholder");
    }
  };

  bindShortcuts = () => {
    const {
      openContextCombo,
      openFilterCombo,
      focusSearchBar
    } = this.props.shortcuts;
    Mousetrap.bindGlobal(openContextCombo, () => {
      if (this.contextCombo && this.contextCombo.current) {
        this.contextCombo.current.focus();
      }
    });
    Mousetrap.bindGlobal(openFilterCombo, () => {
      if (this.filterCombo && this.filterCombo.current) {
        this.filterCombo.current.focus();
      }
    });
    Mousetrap.bindGlobal(focusSearchBar, () => this.focusSearchBox());
  };

  focusSearchBox = () => {
    if (this.searchBox) {
      this.searchBox.focus();
    }
  };

  unbindShortcuts = () => {
    const {
      openContextCombo,
      openFilterCombo,
      focusSearchBar
    } = this.props.shortcuts;
    Mousetrap.unbind(openContextCombo);
    Mousetrap.unbind(openFilterCombo);
    Mousetrap.unbind(focusSearchBar);
  };

  getCategoryGroups = () => {
    const {
      categoryList,
      restrictCategory,
      changeCategory,
      changeCategories
    } = this.props;
    return categoryList.map(c => {
      switch (c.filterTypeId) {
        case 2: {
          const categories = c.categories;
          if (!categories.some(cat => cat.id === -1)) {
            categories.unshift({ id: -1, name: localize("showAllOption") });
          }
          const selectedCategory = categories.find(cat => cat.selected);
          const selectedId = selectedCategory ? selectedCategory.id : -1;
          return (
            <ControlListItem key={c.id}>
              <MultiSelect
                disabled={restrictCategory}
                inlineLabel={`${c.name}:`}
                options={categories}
                selectedValue={selectedId}
                single
                onChange={value => changeCategory(c.id, value)}
              />
            </ControlListItem>
          );
        }
        case 3:
          return (
            <ControlListItem key={c.id}>
              <MultiSelect
                disabled={restrictCategory}
                inlineLabel={`${c.name}:`}
                options={c.categories.filter(cat => cat.id > 0)}
                onChange={selected =>
                  changeCategories(
                    c.id,
                    selected.map(o => o.id)
                  )
                }
              />
            </ControlListItem>
          );
        default:
          return null;
      }
    });
  };

  render() {
    const {
      contextList,
      contextId,
      isModifierActive,
      sortBy,
      isDetailsView,
      reload,
      contentLoading
    } = this.props;

    // need to make a copy or assets.contextList will also add Show all option
    const contexts = [...contextList];
    if (!contexts.some(c => c.id === 0)) {
      contexts.unshift({ id: 0, name: localize("showAllOption") });
    }
    return (
      <ControlBarContainer>
        <ControlListPadded>
          <ControlListItem>
            <SearchBox
              type="search"
              borderColor={this.state.searchTerms.length > 0 ? "#3399FF" : null}
              onKeyPress={this.onSearch}
              onChange={this.onSearch}
              placeholder={this.getSearchPlaceholder()}
              ref={r => {
                this.searchBox = r;
              }}
            />
          </ControlListItem>
          <ControlListItem>
            <MultiSelect
              ref={this.contextCombo}
              inlineLabel={generateShortcutText(
                localize("contextLabel"),
                isModifierActive,
                true
              )}
              options={contexts}
              selectedValue={contextId}
              single
              onChange={value => this.props.changeContext(value)}
            />
          </ControlListItem>
          {this.getCategoryGroups()}
          <ControlListItem>
            <MultiSelect
              ref={this.filterCombo}
              inlineLabel={generateShortcutText(
                localize("filterLabel"),
                isModifierActive,
                true
              )}
              options={[
                { id: FilterOption.NONE, name: localize("noneOption") },
                { id: FilterOption.FAVORITE, name: localize("favoriteOption") }
              ]}
              selectedValue={this.props.filterBy}
              single
              onChange={value => this.props.changeFilterBy(value)}
            />
          </ControlListItem>
          <ControlListItem>
            <MultiSelect
              inlineLabel={localize("sortLabel")}
              options={[
                { id: SortOption.A_Z, name: localize("sortByNameAscOption") },
                { id: SortOption.Z_A, name: localize("sortByNameDescOption") },
                {
                  id: SortOption.MOST_RECENT,
                  name: localize("sortByDateDescOption")
                },
                {
                  id: SortOption.LEAST_RECENT,
                  name: localize("sortByDateAscOption")
                }
              ]}
              selectedValue={sortBy}
              single
              onChange={value => this.props.changeSortBy(value)}
            />
          </ControlListItem>
          <ControlListItem>
            <DetailsListButton
              onClick={this.onToggleDetails}
              active={isDetailsView}
            />
          </ControlListItem>
          <ControlListItem>
            <IconButton
              onClick={() => {
                reload();
              }}
            >
              <FontAwesomeIcon
                fixedWidth
                size="lg"
                spin={contentLoading}
                icon={faSyncAlt}
              />
            </IconButton>
          </ControlListItem>
        </ControlListPadded>
      </ControlBarContainer>
    );
  }
}

Header.defaultProps = {
  visible: true
};

Header.propTypes = {
  filterBy: PropTypes.string.isRequired,
  categoryList: LuciPropTypes.CategoryList.isRequired,
  contextList: LuciPropTypes.ContextList.isRequired,
  changeCategory: PropTypes.func.isRequired,
  changeCategories: PropTypes.func.isRequired,
  contextId: PropTypes.number.isRequired,
  changeContext: PropTypes.func.isRequired,
  changeSearchTerms: PropTypes.func.isRequired,
  changeSortBy: PropTypes.func.isRequired,
  changeFilterBy: PropTypes.func.isRequired,
  toggleDetailsView: PropTypes.func.isRequired,
  assetType: PropTypes.string.isRequired,
  sortBy: PropTypes.string.isRequired,
  restrictCategory: PropTypes.bool.isRequired,
  categoriesLoading: PropTypes.bool.isRequired,
  filterList: LuciPropTypes.FilterList.isRequired,
  shortcuts: PropTypes.shape({
    openContextCombo: PropTypes.arrayOf(PropTypes.string),
    openCategoryCombo: PropTypes.arrayOf(PropTypes.string),
    openFilterCombo: PropTypes.arrayOf(PropTypes.string),
    focusSearchBar: PropTypes.arrayOf(PropTypes.string)
  }).isRequired,
  isModifierActive: PropTypes.bool.isRequired,
  isDetailsView: PropTypes.bool.isRequired,
  visible: PropTypes.bool,
  reload: PropTypes.func.isRequired,
  contentLoading: PropTypes.bool.isRequired
};

const mapStateToProps = state => ({
  categoryList: state.content.categoryList,
  filterBy: state.content.filterBy,
  contextList: state.content.contextList,
  contextId: state.content.contextId,
  assetType: state.content.assetType,
  sortBy: state.content.sortBy,
  restrictCategory: state.content.restrictCategory,
  categoriesLoading: state.content.categoriesLoading,
  filterList: state.content.filterList,
  shortcuts: state.keyShortcuts.shortcuts,
  isModifierActive: state.keyShortcuts.isModifierActive,
  isDetailsView: state.content.isDetailsView,
  contentLoading: state.content.contentLoading
});

const mapActionCreators = {
  changeCategory: contentActions.changeCategory,
  changeCategories: contentActions.changeCategories,
  changeContext: contentActions.changeContext,
  changeSearchTerms: contentActions.changeSearchTerms,
  changeFilterBy: contentActions.changeFilterBy,
  changeSortBy: contentActions.changeSortBy,
  toggleDetailsView: assetsActions.toggleDetailsView,
  reload: contentActions.reload
};

export default connect(mapStateToProps, mapActionCreators, null, {
  withRef: true
})(Header);



// WEBPACK FOOTER //
// ./app/components/ContentSelection/Header.js